<template>
  <div
    class="flex items-center p-2 shadow-sm gap-4 border rounded cursor-pointer"
    @click="onOpenFormClick(pageData)"
  >
    <!-- Title Section -->
    <div class="flex-grow">
      <div class="flex items-center">
        <!-- Icon -->
        <i
          class="pi text-gray pr-2 pl-1 py-3"
          :class="pageData.is_list ? `pi-list` : `pi-file`"
          style="font-size: 20px"
        ></i>

        <!-- Title -->
        <div class="font-medium text-gray-800">
          {{ pageData.label }}
        </div>
        <!-- Additional Info -->
        <div
          class="text-sm text-gray-500 px-2"
          v-if="
            Helper.isValueExist(pageData.min_required_items) && pageData.is_list
          "
        >
          {{ `(${pageData.list_items.length}/${pageData.min_required_items})` }}
        </div>
      </div>
    </div>

    <!-- KNob progress Section -->
    <div class="flex-shrink-0">
      <Knob
        v-model="progressIndicatorValue"
        :valueColor="progressIndicatorValue == 100 ? `LimeGreen` : `Orange`"
        valueTemplate="{value}%"
        :strokeWidth="8"
        :size="60"
        readonly
      />
    </div>
    <!-- Status Section -->
    <div class="flex-shrink-0">
      <Tag
        :severity="Helper.getQCProgressStatusColor(getCurrentStatus())"
        :value="getCurrentStatus()"
        class="text-xs md:text-sm"
      />
    </div>

    <!-- CRUD Options Section -->
    <div class="flex-shrink-0 flex gap-1">
      <Button
        v-if="showCRUDOptions"
        severity="info"
        icon="pi pi-pencil"
        variant="text"
        @click.stop="onItemUpdateClick(pageData)"
      />
      <Button
        v-if="showCRUDOptions"
        severity="danger"
        icon="pi pi-trash"
        variant="text"
        @click.stop="onDeleteItem"
      />
      <!-- Button to preview PDF -->
      <Button
        severity="secondary"
        icon="pi pi-eye"
        variant="text"
        v-tooltip.top="'Preview PDF'"
        @click.stop="onPreviewAsPDF"
      />
    </div>
  </div>
  <!-- for next page -->
  <FormPageDialogComponent
    v-if="localPageData.pageObj"
    :visible="dialogVisible"
    @update:visible="dialogVisible = $event"
    :page="localPageData"
    :selectedPath="[...selectedPath, pageData.id]"
    :selectedLabels="[
      ...selectedLabels,
      { label: pageData.label, id: pageData.id }
    ]"
    @update:page="onUpdatedPageData"
    @update:navigateToBreadcrumb="navigateToPageInTheBreadcrumb"
    :title="localPageData.label"
    :isFullScreen="true"
    :savedResValue="savedResValue"
  />
  <!-- for parent to update same page -->
  <FormPageDialogComponent
    v-if="parentPage.parentPageData"
    :visible="parentPage.isVisible"
    @update:visible="parentPage.isVisible = $event"
    :page="parentPage.parentPageData"
    :selectedPath="selectedPath"
    :selectedLabels="selectedLabels"
    :title="parentPage.title"
    :isFullScreen="false"
    :onSaveClick="parentPage.onSaveClick"
    :savedResValue="savedResValue"
    :ignoreOverridePageValue="true"
    :itemId="parentPage.itemId"
    :updateLocalStore="false"
  />
  <FullScreenOverlayLoader
    :isLoading="loaderObj.isLoading"
    :message="loaderObj.loaderMsg"
  />
  <ErrorDialog
    :visible="errorObj.isVisible"
    :message="errorObj.errorMsg"
    :onConfirm="onErrorDialogClose"
    @update:visible="errorObj.isVisible = $event"
  />
  <PdfPreviewDialog
    :visible="pdfPreview.visible"
    @update:visible="pdfPreview.visible = $event"
    :pdfSrc="pdfPreview.src"
    :fileName="pdfPreview.fileName"
    :hideActions="false"
    @download="onDownloadPreviewPDF"
  />
  <!-- PDF generation progress dialog -->
  <PdfProgressDialog
    :visible="pdfProgress.visible"
    :contextLabel="pdfProgress.contextLabel"
    :label="pdfProgress.label"
    :subLabel="pdfProgress.subLabel"
    :current="pdfProgress.current"
    :total="pdfProgress.total"
    :percent="pdfProgress.percent"
    :funMessage="pdfProgress.funMessage"
    :confirmCancel="pdfProgress.confirmCancel"
    @cancel="onCancelPdfGeneration"
    @confirm-cancel="onConfirmCancelPdf"
    @dismiss-cancel="onDismissCancelPdf"
  />
  <!-- Low fill rate warning dialog -->
  <Dialog
    :visible="lowFillDialog.visible"
    @update:visible="
      (val) => {
        lowFillDialog.visible = val;
      }
    "
    header="Incomplete Form Data"
    :modal="true"
    :closable="false"
    :close-on-escape="false"
    :style="{ width: '540px', maxWidth: '95vw' }"
  >
    <div class="flex flex-col gap-4 py-1">
      <p class="text-sm text-gray-600 leading-relaxed">
        The following
        <strong>
          {{
            lowFillDialog.items.length === 1
              ? "form has"
              : `${lowFillDialog.items.length} forms have`
          }}
        </strong>
        less than 50% of fields filled in. Choose whether to include each form
        with empty values or skip it.
      </p>

      <div class="flex flex-col gap-2 max-h-64 overflow-y-auto pr-1">
        <div
          v-for="item in lowFillDialog.items"
          :key="item.id"
          class="flex items-start gap-3 p-3 rounded-lg border transition-colors"
          :class="
            item.include
              ? 'border-amber-300 bg-amber-50'
              : 'border-gray-200 bg-gray-50'
          "
        >
          <div class="flex-grow min-w-0">
            <!-- Breadcrumb: shows where this form lives in the template tree -->
            <div
              v-if="item.breadcrumb?.length"
              class="flex items-center gap-1 flex-wrap mb-0.5"
            >
              <span
                v-for="(crumb, ci) in item.breadcrumb"
                :key="ci"
                class="flex items-center gap-1 text-xs text-gray-400"
              >
                <i
                  v-if="ci > 0"
                  class="pi pi-angle-right text-[9px] text-gray-300"
                ></i>
                {{ crumb }}
              </span>
            </div>
            <div class="font-medium text-gray-800 text-sm">
              {{ item.label }}
            </div>
            <div
              v-if="item.missingLayout"
              class="flex items-center gap-1 mt-1"
            >
              <i class="pi pi-exclamation-triangle text-red-500 text-xs"></i>
              <span class="text-xs text-red-500 font-medium">
                No PDF layout configured — cannot generate
              </span>
            </div>
            <template v-else>
              <div class="flex items-center gap-2 mt-1.5">
                <ProgressBar
                  :value="item.fillPercent"
                  :style="{ height: '5px', flex: '1 1 0' }"
                />
                <span
                  class="text-xs font-semibold flex-shrink-0 w-9 text-right"
                  :class="
                    item.fillPercent < 25 ? 'text-red-500' : 'text-orange-500'
                  "
                >
                  {{ item.fillPercent }}%
                </span>
              </div>
              <div class="text-xs text-gray-400 mt-1">
                {{ item.filled }} of {{ item.total }} fields have data
              </div>
            </template>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0 pt-0.5">
            <Checkbox
              v-model="item.include"
              :binary="true"
              :inputId="`lf-${item.id}`"
            />
            <label
              :for="`lf-${item.id}`"
              class="text-xs text-gray-600 cursor-pointer select-none whitespace-nowrap"
            >
              Include anyway
            </label>
          </div>
        </div>
      </div>

      <!-- Heads-up shown only when the user opts to include a low-fill form -->
      <div
        v-if="lowFillDialog.items.some((i) => i.include)"
        class="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800"
      >
        <i
          class="pi pi-exclamation-triangle mt-0.5 flex-shrink-0 text-amber-500"
        ></i>
        <span>
          Included forms will be generated with
          <strong>empty fields</strong>
          where data is missing. The resulting PDF may look incomplete.
        </span>
      </div>

      <div class="flex items-center justify-between gap-3 pt-1">
        <span class="text-xs text-gray-400">
          {{ lowFillDialog.items.filter((i) => !i.include).length }} of
          {{ lowFillDialog.items.length }} form(s) will be skipped
        </span>
        <div class="flex gap-2">
          <Button
            label="Cancel All"
            icon="pi pi-times"
            severity="secondary"
            variant="outlined"
            size="small"
            @click="onLowFillCancel"
          />
          <Button
            label="Proceed"
            icon="pi pi-arrow-right"
            iconPos="right"
            severity="primary"
            size="small"
            @click="onLowFillProceed"
          />
        </div>
      </div>
    </div>
  </Dialog>
  <!-- Download choice dialog: shown for list items with multiple child PDFs -->
  <Dialog
    :visible="downloadChoiceDialog.visible"
    @update:visible="downloadChoiceDialog.visible = $event"
    header="Download PDF"
    :modal="true"
    :closable="true"
    :style="{ width: '420px' }"
  >
    <p class="text-gray-600 mb-5">
      How would you like to download the PDF for
      <strong>{{ pageData.label }}</strong>
      ?
    </p>
    <div class="flex gap-3 justify-end flex-wrap">
      <Button
        label="All in one PDF"
        icon="pi pi-file-pdf"
        severity="primary"
        @click="onDownloadChoiceSingle"
      />
      <Button
        label="Separate PDFs"
        icon="pi pi-copy"
        severity="secondary"
        @click="onDownloadChoiceSeparate"
      />
      <Button
        label="Both"
        icon="pi pi-download"
        severity="contrast"
        @click="onDownloadChoiceBoth"
      />
    </div>
  </Dialog>
</template>

<script>
  import { defineComponent, ref, computed, provide } from "vue";
  import Helper from "@/utils/helper";
  import Constants from "@/utils/constants";
  import FullScreenOverlayLoader from "@/components/FullScreenOverlayLoader.vue";
  import ErrorDialog from "@/components/ErrorDialog.vue";
  import FormPageDialogComponent from "./FormPageDialogComponent.vue";
  import PdfPreviewDialog from "@/pages/forms/flra/components/PdfPreviewDialog.vue";
  import PdfProgressDialog from "@/components/PdfProgressDialog.vue";
  import { useFormPageStore } from "@/stores/formPageStore";
  import { useFormTemplateResponseStore } from "@/stores/formTemplateResponseStore";
  import { usePdfLayoutStore } from "@/stores/pdfLayoutsStore";
  import { storeToRefs } from "pinia";
  import { useRoute } from "vue-router";
  import sampleResponseData from "@/assets/response_data/7b3e54f2-7bd2-4aac-945d-4e8caeeb35c5.json";

  const localPdfLayoutModules = import.meta.glob(
    "@/assets/pdf_layouts/*.json",
    { eager: true }
  );
  const localPdfLayouts = Object.fromEntries(
    Object.entries(localPdfLayoutModules).map(([path, mod]) => {
      const id = path
        .split("/")
        .pop()
        .replace(/\.json$/, "");
      return [id, mod.default || mod];
    })
  );

  export default defineComponent({
    name: "ProceduresTemplateComponent",
    components: {
      FullScreenOverlayLoader,
      ErrorDialog,
      FormPageDialogComponent,
      PdfPreviewDialog,
      PdfProgressDialog
    },
    props: {
      status: {
        type: String,
        required: true
      },
      itemIndex: {
        type: Number,
        default: null
      },
      pageData: {
        type: Object,
        required: true
      },
      parentPageData: {
        type: Object
      },
      resId: {
        type: String
      },
      showCRUDOptions: {
        type: Boolean,
        default: false
      },
      onDeleteItem: {
        type: Function
      },
      selectedPath: {
        type: Array,
        default: () => []
      },
      savedResValue: {
        type: Object,
        default: () => ({})
      },
      selectedLabels: {
        type: Array,
        default: () => []
      }
    },
    setup(props, { emit }) {
      // Procedures forms are shared across users. Try local RxDB first
      // (current user's own signatures stay fast); fall back to Supabase for
      // signatures authored by other collaborators that never replicated here.
      provide("signaturesNetworkFallback", true);

      const USE_NETWORK_PDF_PREVIEW = true;
      const USE_STORE_RESPONSE_FOR_PDF_PREVIEW = true;

      const localPageData = ref(props.pageData);

      const route = useRoute();
      const formTemplateResponseStore = useFormTemplateResponseStore();
      const { response } = storeToRefs(formTemplateResponseStore);
      const progressIndicatorValue = computed(() => {
        const store = useFormPageStore();

        if (props.pageData.is_list) {
          // Collect all item IDs to calculate progress
          const itemIds = props.pageData.list_items.map((item) => item.id);
          return store.getFormProgress(props.pageData.id, itemIds);
        } else {
          return Math.round(store.getFormProgress(props.pageData.id));
        }
      });
      // only need to process existing list_items if it's list, on page view no need of that
      if (props.pageData.is_list) {
        // Use path-based lookup to get the correct node from the response tree
        const _currentPageData = Helper.getValueByPath(
          Helper.deepCopy(response.value),
          [...props.selectedPath, localPageData.value.id]
        );
        if (_currentPageData) {
          // Collect items from both ${uuid}_list_items (canonical) and plain list_items (legacy)
          const uuidListItems =
            _currentPageData[`${localPageData.value.id}_list_items`];
          const plainListItems = _currentPageData.list_items;
          const allResponseItems = [
            ...(Array.isArray(uuidListItems) ? uuidListItems : []),
            ...(Array.isArray(plainListItems) ? plainListItems : [])
          ];
          if (allResponseItems.length > 0) {
            // Build a set of existing IDs to avoid duplicates
            const existingIds = new Set(
              localPageData.value.list_items.map((item) => item.id)
            );
            const newItems = allResponseItems.filter(
              (item) => !existingIds.has(item.id)
            );
            if (newItems.length > 0) {
              localPageData.value.list_items = [
                ...localPageData.value.list_items,
                ...newItems
              ];
            }
          }
        }
      }

      const errorObj = ref({
        isVisible: false,
        errorMsg: ""
      });
      const loaderObj = ref({
        isLoading: false,
        loaderMsg: ""
      });
      const dialogVisible = ref(false);
      const pdfPreview = ref({
        visible: false,
        src: null,
        fileName: "procedure.pdf",
        childPdfs: []
      });

      // When set, this callback receives the generated PDF data instead of
      // showing the preview dialog.  Used by the parent's "Download All" flow.
      const _pdfReadyCallback = ref(null);

      const downloadChoiceDialog = ref({ visible: false });

      const pdfProgress = ref({
        visible: false,
        contextLabel: "",
        label: "",
        subLabel: "",
        current: 0,
        total: 0,
        percent: 0,
        cancelled: false,
        confirmCancel: false,
        funMessage: ""
      });

      const lowFillDialog = ref({
        visible: false,
        items: [], // { id, label, fillPercent, filled, total, include }
        resolve: null
      });

      const PDF_FUN_MESSAGES = [
        "Waking up the servers… ☕",
        "Convincing pixels to hold still…",
        "Negotiating with the render engine…",
        "The hamsters are running as fast as they can… 🐹",
        "Applying digital staples…",
        "Teaching bytes to walk in a straight line…",
        "Consulting the ancient PDF scrolls…",
        "Asking the printer if it's ready (it never is)…",
        "Bribing the layout engine with compliments…",
        "Wrangling unruly form fields…",
        "Making sure the margins don't rebel…",
        "Folding digital paper cranes… 🦢",
        "Whispering sweet nothings to the server…",
        "Tiny robots are hand-crafting your PDF… 🤖",
        "Adding extra sprinkles of professionalism… 🍩",
        "Calculating the meaning of life and this PDF…",
        "Almost there. Probably. We think. Maybe.",
        "Your PDF is being photosynthesised… 🌱",
        "Running on hopes, dreams, and network requests…",
        "Doing math that would make your head spin… 🌀"
      ];

      const updatePdfProgress = (label, subLabel, current, total) => {
        pdfProgress.value.label = label;
        pdfProgress.value.subLabel = subLabel ?? "";
        pdfProgress.value.current = current;
        pdfProgress.value.total = total;
        pdfProgress.value.percent =
          total > 0 ? Math.min(100, Math.round((current / total) * 100)) : 0;
        pdfProgress.value.funMessage =
          PDF_FUN_MESSAGES[current % PDF_FUN_MESSAGES.length];
      };

      const onCancelPdfGeneration = () => {
        pdfProgress.value.confirmCancel = true;
      };

      const onConfirmCancelPdf = () => {
        pdfProgress.value.confirmCancel = false;
        pdfProgress.value.cancelled = true;
      };

      const onDismissCancelPdf = () => {
        pdfProgress.value.confirmCancel = false;
      };

      // Returns a Promise that resolves once the user makes a choice in the
      // low-fill warning dialog.  Resolves with { proceed, items }.
      const showLowFillWarning = (lowItems) => {
        return new Promise((resolve) => {
          lowFillDialog.value.items = lowItems.map((it) => ({
            ...it,
            include: false
          }));
          lowFillDialog.value.resolve = resolve;
          lowFillDialog.value.visible = true;
        });
      };

      const onLowFillProceed = () => {
        lowFillDialog.value.visible = false;
        const resolve = lowFillDialog.value.resolve;
        lowFillDialog.value.resolve = null;
        if (resolve)
          resolve({ proceed: true, items: lowFillDialog.value.items });
      };

      const onLowFillCancel = () => {
        lowFillDialog.value.visible = false;
        const resolve = lowFillDialog.value.resolve;
        lowFillDialog.value.resolve = null;
        if (resolve) resolve({ proceed: false, items: [] });
      };

      const sanitizeFileName = (name) => {
        const invalidFileNameChars = /[<>:"/\\|?*]/g;
        const noControlChars = Array.from(String(name || ""))
          .filter((ch) => ch.charCodeAt(0) >= 32)
          .join("");
        const cleaned = noControlChars
          .replace(invalidFileNameChars, "-")
          .replace(/\s+/g, " ")
          .trim();
        // Avoid empty filename
        const base = cleaned.length ? cleaned : "procedure";
        // Keep it reasonably short for most OSes
        return base.slice(0, 120);
      };

      // Traverses a resolved PDF layout and checks how many path-based field
      // values actually exist in formData. Returns { filled, total, percent }.
      const computeLayoutFillRate = (layoutJson, formData) => {
        if (!layoutJson || !formData)
          return { filled: 0, total: 0, percent: 100 };
        const CONTAINER_TYPES = new Set([
          "divider",
          "table",
          "column",
          "row",
          "page",
          "header",
          "footer"
        ]);
        let total = 0;
        let filled = 0;
        const stack = [layoutJson];
        while (stack.length) {
          const cur = stack.pop();
          if (!cur || typeof cur !== "object") continue;
          if (Array.isArray(cur)) {
            for (const v of cur) stack.push(v);
            continue;
          }
          // Count leaf fields whose value is a path array referencing form data.
          // Skip fields whose path passes through a _list_items key — those
          // reference the list container itself and always have data, so they
          // would artificially inflate the "filled" count.
          if (
            cur.type &&
            !CONTAINER_TYPES.has(cur.type) &&
            Array.isArray(cur.value) &&
            cur.value.length > 0 &&
            !cur.value.some(
              (seg) => typeof seg === "string" && seg.endsWith("_list_items")
            )
          ) {
            total++;
            const val = Helper.getValueByPath(formData, cur.value);
            const isEmpty =
              val === null ||
              val === undefined ||
              val === "" ||
              (Array.isArray(val) && val.length === 0);
            if (!isEmpty) filled++;
          }
          // Recurse into all nested properties; skip path arrays to avoid
          // treating path segments as traversal targets.
          for (const [k, v] of Object.entries(cur)) {
            if (k === "value" && Array.isArray(v)) continue;
            if (v && typeof v === "object") stack.push(v);
          }
        }
        return {
          filled,
          total,
          percent: total > 0 ? Math.round((filled / total) * 100) : 100
        };
      };

      const findItemIndexListPathFromLayout = (layout) => {
        // Find a "value" path array that looks like:
        // [..., "<something>_list_items", "ITEM_INDEX", "<field>"]
        // We then use the prefix up to "<something>_list_items" to locate the list in `formData`.
        const stack = [layout];
        while (stack.length) {
          const cur = stack.pop();
          if (!cur) continue;

          if (Array.isArray(cur)) {
            // Only treat arrays of path segments as candidates.
            const itemIndexPos = cur.indexOf("ITEM_INDEX");
            if (itemIndexPos > 0) {
              const maybeListKey = cur[itemIndexPos - 1];
              if (
                typeof maybeListKey === "string" &&
                maybeListKey.endsWith("_list_items")
              ) {
                return cur.slice(0, itemIndexPos); // includes the list key
              }
            }

            for (const v of cur) stack.push(v);
            continue;
          }

          if (typeof cur === "object") {
            for (const v of Object.values(cur)) stack.push(v);
          }
        }
        return null;
      };

      const findItemInAnyList = (data, _itemId) => {
        // Fallback: scan entire response tree for any array containing objects with matching id.
        const stack = [data];
        while (stack.length) {
          const cur = stack.pop();
          if (!cur) continue;

          if (Array.isArray(cur)) {
            const idx = cur.findIndex((it) => it?.id === _itemId);
            if (idx >= 0) {
              return { idx, item: cur[idx] };
            }
            for (const v of cur) stack.push(v);
            continue;
          }

          if (typeof cur === "object") {
            for (const v of Object.values(cur)) stack.push(v);
          }
        }
        return { idx: null, item: null };
      };

      const getItemIndexForLayout = (data, layout, _itemId) => {
        const listPath = findItemIndexListPathFromLayout(layout);
        if (listPath) {
          const list = Helper.getValueByPath(data, listPath);
          if (Array.isArray(list)) {
            const idx = list.findIndex((it) => it?.id === _itemId);
            if (idx >= 0) return idx;
          }
        }

        const fallback = findItemInAnyList(data, _itemId);
        return fallback.idx;
      };

      const getItemLabelForLayout = (data, layout, _itemId) => {
        const listPath = findItemIndexListPathFromLayout(layout);
        if (listPath) {
          const list = Helper.getValueByPath(data, listPath);
          if (Array.isArray(list)) {
            return list.find((it) => it?.id === _itemId)?.label || null;
          }
        }

        const fallback = findItemInAnyList(data, _itemId);
        return fallback.item?.label || null;
      };

      const buildPdfFileNameWithBreadcrumbPrefix = (baseName) => {
        const labels = (props.selectedLabels || [])
          .map((x) => x?.label)
          .filter(Boolean);
        const prefix = labels; // full breadcrumb labels

        const parts = [...prefix, baseName]
          .filter(Boolean)
          .map(sanitizeFileName);
        const joined = parts.filter((p) => p.length).join("_");
        return `${joined || "procedure"}.pdf`;
      };

      const downloadDataUri = async (dataUri, fileName) => {
        const res = await fetch(dataUri);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        try {
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
        } finally {
          URL.revokeObjectURL(url);
        }
      };
      const parentPage = ref({
        isVisible: false,
        parentPageData: {},
        itemId: null,
        title: null,
        onSaveClick: (data) => {
          // reset the item id after save
          parentPage.value.itemId = null;
          emit("update:parentPageData", data);
        }
      });

      const navigateToPageInTheBreadcrumb = async (targetPageId) => {
        // current page id
        const _currentPageId = localPageData.value.id;
        // if target vs current page id is not same then close current dialog
        if (targetPageId !== _currentPageId) {
          dialogVisible.value = false;
          // wait for 100 ms
          await new Promise((resolve) => setTimeout(resolve, 100));
          emit("update:navigateToBreadcrumb", targetPageId);
        }
      };

      const onUpdatedPageData = async (data) => {
        localPageData.value = data;
        console.log("UPDATING STORE DATA AT PROCED....", data.id);
        await useFormPageStore().updateFormPageFromList(localPageData.value);
        emit("update:pageData", data);
      };

      const onOpenFormClick = async (data) => {
        console.log("=== ProceduresTemplateComponent Debug ===", data);

        console.log("new selectedPath:", [
          ...props.selectedPath,
          props.pageData.id
        ]);
        console.log("selectedLabels:", [
          ...props.selectedLabels,
          { label: props.pageData.label, id: props.pageData.id }
        ]);

        try {
          loaderObj.value.isLoading = true;
          loaderObj.value.loaderMsg = "Processing...";
          if (!localPageData.value.page_id)
            throw `No page found for ${Helper.getBoldTxt(data.label)}`;
          const { ...restData } = data;

          localPageData.value = {
            ...restData
          };
          // // checking if it's in pinia store
          const _exisitingPageRes =
            await useFormPageStore().getFormPageFromList(
              localPageData.value.id
            );
          if (_exisitingPageRes) localPageData.value = _exisitingPageRes;
          // if form doesn't exists in local then fetch else use the old one.
          if (!localPageData.value.pageObj) {
            const _pageRes = await useFormPageStore().getFormPageById(
              localPageData.value.page_id,
              true
            );
            if (_pageRes.error) throw _pageRes.error;
            if (_pageRes.data) localPageData.value.pageObj = _pageRes.data;
            // props.showCRUDOptions = false , mean if no need to show then no need to update local progressdata and affect progress indicator
            let updatedData = await useFormPageStore().updateFormPageFromList(
              localPageData.value,
              !(props.showCRUDOptions == false),
              route.params
            );
            if (updatedData) {
              localPageData.value = updatedData;
            }
          }
          emit("update:pageData", localPageData.value);
          dialogVisible.value = true;
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        } finally {
          loaderObj.value.isLoading = false;
        }
      };

      const onErrorDialogClose = () => {
        errorObj.value.isVisible = false;
        errorObj.value.errorMsg = "";
      };

      const onPreviewAsPDF = async () => {
        try {
          console.log(
            "=== Procedures PDF Preview Clicked ===",
            props.selectedPath
          );
          // Keep dialog closed until we have a real PDF to show.
          pdfPreview.value.visible = false;
          pdfPreview.value.src = null;
          pdfPreview.value.fileName = "procedure.pdf";
          pdfPreview.value.childPdfs = [];

          // List items use a dedicated progress dialog; single-leaf items use the
          // generic full-screen loader which is fast enough to not need steps.
          if (props.pageData?.is_list) {
            pdfProgress.value.cancelled = false;
            pdfProgress.value.contextLabel = props.pageData?.label ?? "";
            pdfProgress.value.visible = true;
            updatePdfProgress(
              "Fetching data\u2026",
              props.pageData?.label,
              0,
              1
            );
          } else {
            loaderObj.value.isLoading = true;
            loaderObj.value.loaderMsg = "Generating PDF preview...";
          }

          const pdfLayoutStore = usePdfLayoutStore();
          const templateId = props.selectedPath?.[0];
          const pageId = props.pageData?.page_id;
          console.error("PULLING FOR :", pageId);
          const itemId = props.pageData?.id;
          const findParentContainerIdForItem = (data, targetItemId) => {
            try {
              if (!data || !targetItemId) return null;
              const stack = [data];
              while (stack.length) {
                const cur = stack.pop();
                if (!cur || typeof cur !== "object") continue;

                if (Array.isArray(cur)) {
                  for (const v of cur) stack.push(v);
                  continue;
                }

                for (const [k, v] of Object.entries(cur)) {
                  // Canonical list storage: `<containerId>_list_items: [{ id: <itemId>, ...}]`
                  if (
                    typeof k === "string" &&
                    k.endsWith("_list_items") &&
                    Array.isArray(v) &&
                    v.some((it) => it?.id === targetItemId)
                  ) {
                    return k.slice(0, -"_list_items".length);
                  }

                  if (v && typeof v === "object") stack.push(v);
                }
              }
              return null;
            } catch {
              return null;
            }
          };

          const getParentItemIdForContext = (data, _itemId) => {
            // For list pages, the container is the current page id.
            if (props.pageData?.is_list) return props.pageData?.id ?? null;

            // For leaf pages inside nested lists, compute the immediate container dynamically.
            const resolvedFromResponse = findParentContainerIdForItem(
              data,
              _itemId
            );
            if (resolvedFromResponse) return resolvedFromResponse;

            // Fall back to UI navigation hints.
            return (
              (props.selectedPath || []).slice(-1)[0] ??
              props.parentPageData?.id ??
              null
            );
          };

          const normalizeLayoutContent = (layoutRowOrJson) => {
            const raw = layoutRowOrJson?.content ?? layoutRowOrJson;
            if (!raw) return {};
            if (typeof raw === "string") {
              try {
                return JSON.parse(raw);
              } catch {
                return {};
              }
            }
            return raw;
          };

          const getProceduresAttachments = (
            data,
            layoutJson,
            _itemId,
            _itemIndex,
            _parentItemId
          ) => {
            try {
              const mergeBlock = (layoutJson?.additionalContent || []).find(
                (b) =>
                  b?.type === "conditional_mergePdf" && Array.isArray(b?.value)
              );
              if (!mergeBlock?.value?.length) return [];

              const resolvedPath = Helper.replaceTokensDeep(
                Helper.deepCopy(mergeBlock.value),
                {
                  ITEM_ID: _itemId,
                  ITEM_INDEX: _itemIndex,
                  PARENT_ITEM_ID: _parentItemId
                }
              );

              let cur = data;
              for (const key of resolvedPath) {
                if (cur == null) return [];
                cur = cur?.[key];
              }

              return Array.isArray(cur) ? cur : [];
            } catch {
              return [];
            }
          };

          const coerceLayoutToUseItemIndexToken = (layoutJson) => {
            // Some older layouts may still hardcode list index `0` for *_list_items access.
            // If we detect a "path array" like: [..., "<something>_list_items", 0, "<field>"]
            // replace the `0` with "ITEM_INDEX" so our runtime token replacement can work.
            const stack = [layoutJson];
            while (stack.length) {
              const cur = stack.pop();
              if (!cur) continue;

              if (Array.isArray(cur)) {
                for (let i = 0; i < cur.length - 2; i++) {
                  if (
                    typeof cur[i] === "string" &&
                    cur[i].endsWith("_list_items") &&
                    cur[i + 1] === 0
                  ) {
                    cur[i + 1] = "ITEM_INDEX";
                  }
                }
                for (const v of cur) stack.push(v);
                continue;
              }

              if (typeof cur === "object") {
                for (const v of Object.values(cur)) stack.push(v);
              }
            }
            return layoutJson;
          };

          const fetchLayoutFromNetworkByPageId = async (_pageId) => {
            const layoutRes = await pdfLayoutStore.getPdfLayout({
              fetchDeleted: false,
              searchTxt: null,
              sortField: null,
              sortOrder: 0,
              page: 0,
              rows: 1,
              fetchAll: true,
              queryParams: [
                { field: "name", value: _pageId },
                { field: "parent_template_id", value: templateId }
              ]
            });
            return coerceLayoutToUseItemIndexToken(
              normalizeLayoutContent(layoutRes)
            );
          };

          const getLocalLayoutByPageId = (_pageId) => {
            const layout = _pageId ? localPdfLayouts[_pageId] : null;
            if (!layout) {
              throw new Error(
                `Template not found for page ${Helper.getBoldTxt(
                  _pageId || "unknown"
                )}`
              );
            }
            return coerceLayoutToUseItemIndexToken(Helper.deepCopy(layout));
          };

          // Default (local) mode for fast iteration without DB calls.
          let layoutData = null;
          let formData = USE_STORE_RESPONSE_FOR_PDF_PREVIEW
            ? Helper.deepCopy(response.value || {})
            : Helper.deepCopy(sampleResponseData || {});

          if (USE_NETWORK_PDF_PREVIEW) {
            if (props.pageData?.is_list) {
              if (pdfProgress.value.cancelled) throw new Error("__cancelled__");
              updatePdfProgress(
                "Fetching PDF layout & response\u2026",
                props.pageData?.label,
                0,
                1
              );
            } else {
              loaderObj.value.loaderMsg = "Fetching PDF layout & response...";
            }

            // Always fetch response from network.
            // Layout fetch depends on list vs single:
            // - single: fetch by `props.pageData.page_id`
            // - list: fetch per child item (later) since children can have different page_ids
            if (!props.pageData?.is_list) {
              layoutData = await fetchLayoutFromNetworkByPageId(pageId);
            }

            const responseRes =
              await formTemplateResponseStore.getFormTemplateResponseById(
                props.resId,
                false,
                false
              );
            if (responseRes?.error) throw responseRes.error;
            formData = Helper.deepCopy(responseRes?.data?.response || {});
          } else if (!props.pageData?.is_list) {
            layoutData = getLocalLayoutByPageId(pageId);
          }

          // Build a signatures array compatible with existing pdf signature renderer.
          // This is additive and does not affect other templates.
          const rootNode =
            formData?.["78d84465-2cf4-498b-905d-d930f8007a57"]?.[
              "c0ede840-b21a-48df-b36c-a07d58e3fb3a"
            ] || {};
          const updatedByName = rootNode.updated_by_name || "Updated By";

          const findSignatureForName = (node, name) => {
            const stack = [node];
            while (stack.length) {
              const cur = stack.pop();
              if (!cur || typeof cur !== "object") continue;

              if (cur.attendeeName === name && Helper.hasSignature(cur)) {
                return cur;
              }

              if (Array.isArray(cur)) {
                for (const v of cur) stack.push(v);
              } else {
                for (const v of Object.values(cur)) stack.push(v);
              }
            }
            return null;
          };

          const sigObj = findSignatureForName(formData, updatedByName);
          const sigEntry = sigObj
            ? {
                attendeeName: updatedByName,
                attendeeType: "employee",
                ...(sigObj.signatureData
                  ? { signatureData: sigObj.signatureData }
                  : {}),
                ...(sigObj.isSignatureRef
                  ? { id: sigObj.id, isSignatureRef: true }
                  : {})
              }
            : null;
          formData.procedures_signatures = sigEntry
            ? [sigEntry, { ...sigEntry }]
            : [];

          if (props.pageData?.is_list) {
            // Recursively merge response-stored list items into nested sub-lists
            // so dynamically added forms are visible when flattening from the
            // master-list level (the setup code only augments the current level).
            const basePath = [...props.selectedPath, props.pageData.id];
            const augmentListItemsDeep = (items, path) => {
              return (Array.isArray(items) ? items : []).map((item) => {
                if (!item?.is_list || !item?.id) return item;
                const itemPath = [...path, item.id];
                const responseNode = Helper.getValueByPath(formData, itemPath);
                let augList = [...(item.list_items || [])];
                if (responseNode) {
                  const uuidItems = Array.isArray(
                    responseNode[`${item.id}_list_items`]
                  )
                    ? responseNode[`${item.id}_list_items`]
                    : [];
                  const plainItems = Array.isArray(responseNode.list_items)
                    ? responseNode.list_items
                    : [];
                  const existingIds = new Set(
                    augList.map((i) => i?.id).filter(Boolean)
                  );
                  const newItems = [...uuidItems, ...plainItems].filter(
                    (i) => i?.id && !existingIds.has(i.id)
                  );
                  if (newItems.length) augList = [...augList, ...newItems];
                }
                return {
                  ...item,
                  list_items: augmentListItemsDeep(augList, itemPath)
                };
              });
            };
            const augmentedListItems = augmentListItemsDeep(
              props.pageData?.list_items,
              basePath
            );

            // topLevelId tracks which direct child of the master list each leaf
            // belongs to so we can generate per-child PDFs for separate download.
            const flattenLeafPages = (
              items,
              seenIds = new Set(),
              parentItemId = props.pageData?.id ?? null,
              topLevelId = null,
              labelPath = []
            ) => {
              const out = [];
              const safeItems = Array.isArray(items) ? items : [];

              for (const it of safeItems) {
                if (!it) continue;
                if (it?.id && seenIds.has(it.id)) continue;
                if (it?.id) seenIds.add(it.id);

                if (it?.is_list) {
                  out.push(
                    ...flattenLeafPages(
                      it?.list_items,
                      seenIds,
                      it?.id ?? null,
                      topLevelId ?? it?.id ?? null,
                      [...labelPath, it?.label || ""]
                    )
                  );
                } else {
                  out.push({
                    ...it,
                    parentItemId,
                    topLevelId: topLevelId ?? it?.id ?? null,
                    labelPath
                  });
                }
              }

              return out;
            };

            let leafItems = flattenLeafPages(augmentedListItems);
            let leafItemIds = leafItems.map((it) => it?.id).filter(Boolean);

            if (!leafItemIds.length) {
              throw new Error(
                `No pages found inside ${Helper.getBoldTxt(props.pageData?.label)}`
              );
            }

            // Shared layout cache used by both pre-scan and the generation loop.
            const layoutCacheByPageId = {};

            // === PRE-SCAN: evaluate fill rates before starting PDF generation ===
            // This lets us warn the user about incomplete forms upfront rather
            // than generating PDFs with mostly empty fields silently.
            updatePdfProgress(
              "Analyzing forms\u2026",
              "Checking data completeness",
              0,
              leafItems.length
            );
            const lowFillEntries = [];
            for (let si = 0; si < leafItems.length; si++) {
              if (pdfProgress.value.cancelled) throw new Error("__cancelled__");
              const scanItem = leafItems[si];
              const scanPageId = scanItem?.page_id;
              if (!scanPageId) {
                // Surface the missing page_id before generation so the user can
                // decide whether to skip this item rather than hitting a hard
                // error mid-generation.
                lowFillEntries.push({
                  id: scanItem.id || `item-${si}`,
                  label: scanItem.label || `Form ${si + 1}`,
                  breadcrumb: scanItem.labelPath || [],
                  fillPercent: 0,
                  filled: 0,
                  total: 0,
                  missingLayout: true
                });
                continue;
              }
              if (!layoutCacheByPageId[scanPageId]) {
                layoutCacheByPageId[scanPageId] = USE_NETWORK_PDF_PREVIEW
                  ? await fetchLayoutFromNetworkByPageId(scanPageId)
                  : getLocalLayoutByPageId(scanPageId);
              }
              const rawScanLayout = layoutCacheByPageId[scanPageId];
              const scanItemIdx =
                getItemIndexForLayout(formData, rawScanLayout, scanItem.id) ??
                0;
              const scanParentId =
                scanItem.parentItemId ??
                findParentContainerIdForItem(formData, scanItem.id) ??
                props.pageData?.id ??
                null;
              const resolvedScanLayout = Helper.replaceTokensDeep(
                Helper.deepCopy(rawScanLayout),
                {
                  ITEM_ID: scanItem.id,
                  ITEM_INDEX: scanItemIdx,
                  PARENT_ITEM_ID: scanParentId
                }
              );
              const fillRate = computeLayoutFillRate(
                resolvedScanLayout,
                formData
              );
              if (fillRate.total > 0 && fillRate.percent < 50) {
                lowFillEntries.push({
                  id: scanItem.id,
                  label: scanItem.label || `Form ${si + 1}`,
                  breadcrumb: scanItem.labelPath || [],
                  fillPercent: fillRate.percent,
                  filled: fillRate.filled,
                  total: fillRate.total
                });
              }
            }

            if (lowFillEntries.length > 0) {
              // Pause the progress dialog while we await the user's decision.
              pdfProgress.value.visible = false;
              const lowFillResult = await showLowFillWarning(lowFillEntries);
              if (!lowFillResult.proceed) throw new Error("__cancelled__");

              const skippedIds = new Set(
                lowFillResult.items
                  // Skip items the user unchecked OR items with no layout (ungenerable).
                  .filter((it) => !it.include || it.missingLayout)
                  .map((it) => it.id)
              );
              if (skippedIds.size > 0) {
                leafItems = leafItems.filter((it) => !skippedIds.has(it.id));
                leafItemIds = leafItems.map((it) => it?.id).filter(Boolean);
                if (leafItems.length === 0) {
                  // User proceeded but didn't include any low-fill items.
                  // Surface a warning toast so they understand why nothing
                  // was generated, then bail out silently.
                  Helper.showToast(
                    "Nothing to generate",
                    "No items were selected — there's no data to build a PDF from.",
                    "warn"
                  );
                  throw new Error("__cancelled__");
                }
              }
              pdfProgress.value.visible = true;
            }
            // === END PRE-SCAN ===

            // Compute direct children early so we know the full step count upfront.
            const directChildren = Array.isArray(props.pageData?.list_items)
              ? props.pageData.list_items.filter(Boolean)
              : [];
            const effectiveChildCount =
              directChildren.length > 1 ? directChildren.length : 0;
            // Steps: 1 fetch + N leaf PDFs + 1 merge + M per-child PDFs
            const totalPdfSteps =
              1 + leafItemIds.length + 1 + effectiveChildCount;

            // Compute the final PDF base name now so the progress dialog title
            // already matches what the download will be called.
            const _firstSec = directChildren[0]?.label?.split(" ")[0];
            const _lastSec =
              directChildren[directChildren.length - 1]?.label?.split(" ")[0];
            const sectionRange =
              _firstSec && _firstSec !== _lastSec
                ? `${_firstSec}-${_lastSec}`
                : (_firstSec ?? null);
            const pdfBaseName = sectionRange
              ? `${sanitizeFileName(props.pageData?.label)} - ${sectionRange}`
              : sanitizeFileName(props.pageData?.label);
            pdfProgress.value.contextLabel = pdfBaseName;

            updatePdfProgress(
              `Generating PDF 1 of ${leafItemIds.length}\u2026`,
              leafItems[0]?.label ?? "",
              1,
              totalPdfSteps
            );

            const pdfParts = [];
            for (let i = 0; i < leafItemIds.length; i++) {
              if (pdfProgress.value.cancelled) throw new Error("__cancelled__");
              const currentItemId = leafItemIds[i];
              const currentItem = leafItems[i] || {};
              updatePdfProgress(
                `Generating PDF ${i + 1} of ${leafItemIds.length}\u2026`,
                currentItem?.label ?? "",
                1 + i,
                totalPdfSteps
              );
              const currentPageId = currentItem?.page_id;
              const currentParentItemId =
                currentItem?.parentItemId ??
                props.pageData?.id ??
                findParentContainerIdForItem(formData, currentItemId) ??
                null;

              if (!currentPageId) {
                throw new Error(
                  `Missing page_id for ${Helper.getBoldTxt(
                    currentItem?.label || `item ${i + 1}`
                  )}`
                );
              }
              if (!layoutCacheByPageId[currentPageId]) {
                layoutCacheByPageId[currentPageId] = USE_NETWORK_PDF_PREVIEW
                  ? await fetchLayoutFromNetworkByPageId(currentPageId)
                  : getLocalLayoutByPageId(currentPageId);
              }
              const currentLayoutData = layoutCacheByPageId[currentPageId];

              const currentItemIndex =
                getItemIndexForLayout(
                  formData,
                  currentLayoutData,
                  currentItemId
                ) ?? 0;
              const perItemLayout = Helper.replaceTokensDeep(
                Helper.deepCopy(currentLayoutData || {}),
                {
                  ITEM_ID: currentItemId,
                  ITEM_INDEX: currentItemIndex,
                  PARENT_ITEM_ID: currentParentItemId
                }
              );
              const perItemFormData = Helper.deepCopy(formData || {});
              perItemFormData.procedures_attachments = getProceduresAttachments(
                perItemFormData,
                perItemLayout,
                currentItemId,
                currentItemIndex,
                currentParentItemId
              );
              const partPdfDataUri = await pdfLayoutStore.generatePDFBase64(
                perItemFormData,
                perItemLayout,
                undefined,
                { networkFallback: true }
              );

              pdfParts.push({
                value: partPdfDataUri,
                name: `${sanitizeFileName(props.pageData?.label)}-${i + 1}.pdf`,
                type: "application/pdf",
                _topLevelId: currentItem?.topLevelId ?? null
              });
            }

            // Merge: use the first PDF as base, then append the remaining PDFs.
            // We'll use the first item's layout for the base render.
            const firstItemId = leafItemIds[0];
            const firstItem = leafItems[0] || {};
            const firstPageId = firstItem?.page_id;
            if (!firstPageId) throw new Error("Missing page_id for first item");
            let firstLayoutData = layoutCacheByPageId[firstPageId];
            if (!firstLayoutData) {
              firstLayoutData = USE_NETWORK_PDF_PREVIEW
                ? await fetchLayoutFromNetworkByPageId(firstPageId)
                : getLocalLayoutByPageId(firstPageId);
              layoutCacheByPageId[firstPageId] = firstLayoutData;
            }

            const firstItemIndex =
              getItemIndexForLayout(formData, firstLayoutData, firstItemId) ??
              0;
            const firstParentItemId =
              firstItem?.parentItemId ??
              findParentContainerIdForItem(formData, firstItemId) ??
              props.pageData?.id ??
              null;
            const mergedLayout = Helper.replaceTokensDeep(
              Helper.deepCopy(firstLayoutData || {}),
              {
                ITEM_ID: firstItemId,
                ITEM_INDEX: firstItemIndex,
                PARENT_ITEM_ID: firstParentItemId
              }
            );
            mergedLayout.additionalContent = Array.isArray(
              mergedLayout.additionalContent
            )
              ? [...mergedLayout.additionalContent]
              : [];

            if (pdfParts.length > 1) {
              mergedLayout.additionalContent.push({
                type: "mandatory_mergePdf",
                value: pdfParts.slice(1)
              });
            }

            const mergedFormData = Helper.deepCopy(formData || {});
            mergedFormData.procedures_attachments = getProceduresAttachments(
              mergedFormData,
              mergedLayout,
              firstItemId,
              firstItemIndex,
              firstParentItemId
            );

            updatePdfProgress(
              "Merging PDFs\u2026",
              "",
              1 + leafItemIds.length,
              totalPdfSteps
            );
            if (pdfProgress.value.cancelled) throw new Error("__cancelled__");

            const mergedPdfDataUri = await pdfLayoutStore.generatePDFBase64(
              mergedFormData,
              mergedLayout,
              undefined,
              { networkFallback: true }
            );

            // Pre-generate one merged PDF per direct child for the "separate
            // download" option.  We reuse already-generated pdfParts (grouped
            // by topLevelId) and only regenerate each child's base render —
            // the remaining parts are appended via mandatory_mergePdf.
            const childPdfsArr = [];
            if (directChildren.length > 1) {
              for (const child of directChildren) {
                if (pdfProgress.value.cancelled)
                  throw new Error("__cancelled__");
                updatePdfProgress(
                  `Preparing section ${childPdfsArr.length + 1} of ${directChildren.length}\u2026`,
                  child?.label ?? "",
                  1 + leafItemIds.length + 1 + childPdfsArr.length,
                  totalPdfSteps
                );
                if (!child?.id) continue;
                const childLeafIndices = leafItems.reduce((acc, it, idx) => {
                  if (it?.topLevelId === child.id) acc.push(idx);
                  return acc;
                }, []);
                if (!childLeafIndices.length) continue;

                const firstIdx = childLeafIndices[0];
                const firstChildLeaf = leafItems[firstIdx] || {};
                const firstChildPageId = firstChildLeaf.page_id;
                if (!firstChildPageId) continue;

                if (!layoutCacheByPageId[firstChildPageId]) {
                  layoutCacheByPageId[firstChildPageId] =
                    USE_NETWORK_PDF_PREVIEW
                      ? await fetchLayoutFromNetworkByPageId(firstChildPageId)
                      : getLocalLayoutByPageId(firstChildPageId);
                }
                const firstChildLayout = layoutCacheByPageId[firstChildPageId];
                const firstChildItemIndex =
                  getItemIndexForLayout(
                    formData,
                    firstChildLayout,
                    firstChildLeaf.id
                  ) ?? 0;
                const firstChildParentItemId =
                  firstChildLeaf.parentItemId ??
                  findParentContainerIdForItem(formData, firstChildLeaf.id) ??
                  null;

                const childMergedLayout = Helper.replaceTokensDeep(
                  Helper.deepCopy(firstChildLayout || {}),
                  {
                    ITEM_ID: firstChildLeaf.id,
                    ITEM_INDEX: firstChildItemIndex,
                    PARENT_ITEM_ID: firstChildParentItemId
                  }
                );
                childMergedLayout.additionalContent = Array.isArray(
                  childMergedLayout.additionalContent
                )
                  ? [...childMergedLayout.additionalContent]
                  : [];

                const childRemainingParts = childLeafIndices
                  .slice(1)
                  .map((i) => pdfParts[i])
                  .filter(Boolean);
                if (childRemainingParts.length > 0) {
                  childMergedLayout.additionalContent.push({
                    type: "mandatory_mergePdf",
                    value: childRemainingParts
                  });
                }

                const childFormData = Helper.deepCopy(formData || {});
                childFormData.procedures_attachments = getProceduresAttachments(
                  childFormData,
                  childMergedLayout,
                  firstChildLeaf.id,
                  firstChildItemIndex,
                  firstChildParentItemId
                );

                const childPdfUri = await pdfLayoutStore.generatePDFBase64(
                  childFormData,
                  childMergedLayout,
                  undefined,
                  { networkFallback: true }
                );
                childPdfsArr.push({
                  src: childPdfUri,
                  fileName: `${sanitizeFileName(props.pageData?.label)} - ${sanitizeFileName((child.label || `Section ${childPdfsArr.length + 1}`).split(" ")[0])}.pdf`
                });
              }
            }

            pdfPreview.value.fileName = `${pdfBaseName}.pdf`;
            pdfPreview.value.childPdfs = childPdfsArr;
            pdfPreview.value.src = mergedPdfDataUri;
            updatePdfProgress("Done!", "", totalPdfSteps, totalPdfSteps);
            pdfProgress.value.visible = false;
            if (_pdfReadyCallback.value) {
              const cb = _pdfReadyCallback.value;
              _pdfReadyCallback.value = null;
              cb({ ...pdfPreview.value });
            } else {
              pdfPreview.value.visible = true;
            }
          } else {
            // Replace all `ITEM_ID` placeholders with the current page item id.
            // This makes the same PDF layout reusable across different procedure items.
            layoutData = Helper.replaceTokensDeep(
              Helper.deepCopy(layoutData || {}),
              {
                ITEM_ID: itemId,
                ITEM_INDEX:
                  getItemIndexForLayout(formData, layoutData, itemId) ?? 0,
                PARENT_ITEM_ID: getParentItemIdForContext(formData, itemId)
              }
            );

            // Check fill rate; warn the user if more than 50% of fields are empty.
            const singleFillRate = computeLayoutFillRate(layoutData, formData);
            if (singleFillRate.total > 0 && singleFillRate.percent < 50) {
              loaderObj.value.isLoading = false;
              const singleFillResult = await showLowFillWarning([
                {
                  id: itemId,
                  label: props.pageData?.label || "this form",
                  fillPercent: singleFillRate.percent,
                  filled: singleFillRate.filled,
                  total: singleFillRate.total
                }
              ]);
              if (
                !singleFillResult.proceed ||
                !singleFillResult.items[0]?.include
              ) {
                throw new Error("__cancelled__");
              }
              loaderObj.value.isLoading = true;
              loaderObj.value.loaderMsg = "Generating PDF preview...";
            }

            // For conditional visibility of the "attached documents" notice in the template.
            const itemIndex =
              getItemIndexForLayout(formData, layoutData, itemId) ?? 0;
            formData.procedures_attachments = getProceduresAttachments(
              formData,
              layoutData,
              itemId,
              itemIndex,
              getParentItemIdForContext(formData, itemId)
            );

            const pdfDataUri = await pdfLayoutStore.generatePDFBase64(
              formData,
              layoutData,
              undefined,
              { networkFallback: true }
            );
            const currentItemLabel =
              getItemLabelForLayout(formData, layoutData, itemId) ||
              props.pageData?.label;
            pdfPreview.value.fileName =
              buildPdfFileNameWithBreadcrumbPrefix(currentItemLabel);
            pdfPreview.value.src = pdfDataUri;
            if (_pdfReadyCallback.value) {
              const cb = _pdfReadyCallback.value;
              _pdfReadyCallback.value = null;
              cb({ ...pdfPreview.value });
            } else {
              pdfPreview.value.visible = true;
            }
          }
        } catch (error) {
          pdfPreview.value.visible = false;
          pdfProgress.value.visible = false;

          const isCancelled =
            error?.message === "__cancelled__" || pdfProgress.value.cancelled;

          // In "Download All" mode, route the outcome back to the caller via the
          // callback so the parent can decide how to handle errors / cancellations.
          if (_pdfReadyCallback.value) {
            const cb = _pdfReadyCallback.value;
            _pdfReadyCallback.value = null;
            pdfProgress.value.cancelled = false;
            cb({
              src: null,
              childPdfs: [],
              fileName: "",
              cancelled: isCancelled,
              error: isCancelled ? null : error?.message || String(error)
            });
            return;
          }

          // Normal (single-item preview) mode.
          if (isCancelled) {
            pdfProgress.value.cancelled = false;
            return;
          }
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        } finally {
          pdfProgress.value.visible = false;
          pdfProgress.value.cancelled = false;
          pdfProgress.value.confirmCancel = false;
          loaderObj.value.isLoading = false;
        }
      };

      const onDownloadPreviewPDF = async () => {
        try {
          if (!pdfPreview.value?.src) return;
          // For list items with multiple child PDFs show a download choice dialog.
          if (
            props.pageData?.is_list &&
            (pdfPreview.value.childPdfs?.length ?? 0) > 1
          ) {
            downloadChoiceDialog.value.visible = true;
            return;
          }
          await downloadDataUri(
            pdfPreview.value.src,
            pdfPreview.value.fileName || "procedure.pdf"
          );
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        }
      };

      const onDownloadChoiceSingle = async () => {
        downloadChoiceDialog.value.visible = false;
        try {
          await downloadDataUri(
            pdfPreview.value.src,
            pdfPreview.value.fileName || "procedure.pdf"
          );
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        }
      };

      const onDownloadChoiceSeparate = async () => {
        downloadChoiceDialog.value.visible = false;
        try {
          loaderObj.value.isLoading = true;
          loaderObj.value.loaderMsg = "Downloading separate PDFs...";
          for (const childPdf of pdfPreview.value.childPdfs || []) {
            if (childPdf?.src && childPdf?.fileName) {
              await downloadDataUri(childPdf.src, childPdf.fileName);
            }
          }
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        } finally {
          loaderObj.value.isLoading = false;
        }
      };

      const onDownloadChoiceBoth = async () => {
        downloadChoiceDialog.value.visible = false;
        try {
          loaderObj.value.isLoading = true;
          loaderObj.value.loaderMsg = "Downloading PDFs...";
          await downloadDataUri(
            pdfPreview.value.src,
            pdfPreview.value.fileName || "procedure.pdf"
          );
          for (const childPdf of pdfPreview.value.childPdfs || []) {
            if (childPdf?.src && childPdf?.fileName) {
              await downloadDataUri(childPdf.src, childPdf.fileName);
            }
          }
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        } finally {
          loaderObj.value.isLoading = false;
        }
      };
      // function for handling edit
      const onItemUpdateClick = async (data) => {
        try {
          parentPage.value.title = `Update Form`;
          if (data.id) parentPage.value.itemId = data.id;
          let setInitialData =
            Object.keys(parentPage.value.parentPageData).length === 0;
          if (props.parentPageData && setInitialData) {
            parentPage.value.parentPageData = {
              pageObj: Helper.deepCopy(props.parentPageData)
            };
          }
          // if it's update data
          if (data) {
            const processedFields = Helper.processFieldValues(
              Helper.deepCopy(parentPage.value.parentPageData.pageObj.fields),
              data
            );
            parentPage.value.parentPageData.pageObj.fields = JSON.parse(
              JSON.stringify(processedFields)
            );
          }
          parentPage.value.isVisible = true;
        } catch (error) {
          errorObj.value.isVisible = true;
          errorObj.value.errorMsg = error;
        }
      };

      // Called by the parent "Download All PDFs" flow.
      // Runs the same generation as onPreviewAsPDF but resolves with the result
      // object instead of showing the preview dialog.
      // Shape: { src, childPdfs, fileName }           – success
      //        { cancelled: true, error: null, src: null } – user cancelled
      //        { cancelled: false, error: '…', src: null } – generation error
      const generateAndGetPdfData = () => {
        return new Promise((resolve) => {
          _pdfReadyCallback.value = (data) => resolve(data);
          onPreviewAsPDF().then(() => {
            // The callback is always called either from the success path or from
            // the catch block.  If somehow it's still set here (e.g. a future
            // code path), treat it as cancelled.
            if (_pdfReadyCallback.value) {
              _pdfReadyCallback.value = null;
              resolve({
                src: null,
                childPdfs: [],
                fileName: "",
                cancelled: true,
                error: null
              });
            }
          });
        });
      };

      const getCurrentStatus = () => {
        if (progressIndicatorValue.value == 100) {
          return Constants.enums.PROGRESS_STATUS.COMPLETE;
        } else if (progressIndicatorValue.value == 0) {
          return Constants.enums.PROGRESS_STATUS.NOT_STARTED;
        } else {
          return Constants.enums.PROGRESS_STATUS.IN_PROGRESS;
        }
      };

      return {
        localPageData,
        progressIndicatorValue,
        Helper,
        Constants,
        loaderObj,
        errorObj,
        dialogVisible,
        pdfPreview,
        pdfProgress,
        lowFillDialog,
        downloadChoiceDialog,
        parentPage,
        onCancelPdfGeneration,
        onConfirmCancelPdf,
        onDismissCancelPdf,
        onLowFillProceed,
        onLowFillCancel,
        onOpenFormClick,
        onErrorDialogClose,
        onPreviewAsPDF,
        onDownloadPreviewPDF,
        onDownloadChoiceSingle,
        onDownloadChoiceSeparate,
        onDownloadChoiceBoth,
        onItemUpdateClick,
        getCurrentStatus,
        onUpdatedPageData,
        navigateToPageInTheBreadcrumb,
        generateAndGetPdfData
      };
    }
  });
</script>
